import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import HorizontalTabs from '../elements/horizontal-tabs.element';
import { passwordRegexConstant, phoneRegexConstant } from '../../constants';
import { register, sendOTP, verifyOTP } from '../../apis';
import { AlertType, LoginResponse } from '../../utilities/types';
import CustomAlert from '../elements/custom-alert.element';
import { login } from '../../store/slices/auth.slice';
import { getApiErrorMessage } from '../../utilities/helper';

interface RegisterFormProps {
  onSuccess: () => void;
  loginCallback?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, loginCallback }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<{
    name: string;
    email: string;
    mobile_number: string;
    otp: string;
    password: string;
    confirm_password: string;
    terms: boolean;
  }> | null>(null);
  const [role, setRole] = useState('buyer');
  const [step, setStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });
  const [showResendOtpLink, setShowResendOtpLink] = useState(false);

  // state for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const doSendOTP = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    setShowResendOtpLink(false);
    try {
      const response = await sendOTP('mobile', values.mobile_number);
      if (response.status) {
        toggleAlertMessage('OTP has been sent to you mobile number.', 'info');
        setStep(2);
        setTimeout(
          () => {
            setShowResendOtpLink(true);
          },
          1 * 60 * 1000
        );
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const doVerifyOTP = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = await verifyOTP('mobile', values.mobile_number, values.otp);
      if (response.status) {
        setStep(3);
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const createAccount = async () => {
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const response = await register({ user_type: role, ...values });
      if (response.status) {
        const { user, token } = response.data as LoginResponse;
        const userWithRequiredFields = {
          id: user.id,
          name: user.name,
          email: user.email,
          ...user,
        };
        dispatch(login({ user: { ...userWithRequiredFields }, token }));
        onSuccess();
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const onsubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (step === 1) {
      doSendOTP();
    } else if (step === 2) {
      doVerifyOTP();
    } else {
      createAccount();
    }
  };

  const validationSchemaObject: { [key: string]: any } = {
    mobile_number: Yup.string()
      .matches(phoneRegexConstant, 'Phone must be 10 digits')
      .required('Phone number is required'),
  };
  if (step === 2) {
    validationSchemaObject.otp = Yup.string().required('OTP is required');
  } else if (step === 3) {
    validationSchemaObject.name = Yup.string().required('Full Name is required');
    validationSchemaObject.email = Yup.string()
      .email('Invalid email')
      .required('Email is required');
    validationSchemaObject.password = Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required')
      .matches(
        passwordRegexConstant,
        'Password must contain at least one letter, one number and one special character'
      );
    validationSchemaObject.confirm_password = Yup.string()
      .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
      .required('Confirm Password is required');
    validationSchemaObject.terms = Yup.boolean().oneOf(
      [true],
      'You must accept terms & conditions'
    );
  }

  const validationSchema = Yup.object().shape(validationSchemaObject);

  return (
    <Box>
      <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
        Create Account
      </Typography>
      <Typography align="center" color="text.secondary" sx={{ mb: 2 }}>
        Join Scrap Auction as a buyer or seller
      </Typography>
      {step === 3 && (
        <HorizontalTabs
          items={[
            { label: 'Buyer', value: 'buyer' },
            { label: 'Seller', value: 'seller' },
          ]}
          selectedTab={role}
          onChange={(newValue: string | number) => {
            setRole(newValue as string);
          }}
        />
      )}

      {/* Formik Form */}
      <Formik
        innerRef={formikRef}
        validateOnMount
        initialValues={{
          name: '',
          email: '',
          mobile_number: '',
          otp: '',
          password: '',
          confirm_password: '',
          terms: false,
        }}
        validationSchema={validationSchema}
        onSubmit={() => {
          // console.log('Form Submitted:');
        }}
      >
        {({ errors, touched, handleChange, values, isValid, dirty }) => (
          <Form>
            <Field
              as={TextField}
              name="mobile_number"
              label="Mobile Number *"
              fullWidth
              margin="normal"
              value={values.mobile_number}
              onChange={handleChange}
              error={touched.mobile_number && Boolean(errors.mobile_number)}
              helperText={touched.mobile_number && errors.mobile_number}
            />
            {step === 2 && (
              <>
                <Field
                  as={TextField}
                  name="otp"
                  label="OTP *"
                  fullWidth
                  margin="normal"
                  value={values.otp}
                  onChange={handleChange}
                  error={touched.otp && Boolean(errors.otp)}
                  helperText={touched.otp && errors.otp}
                />
                {showResendOtpLink && (
                  <Typography
                    sx={{
                      m: 0,
                      textAlign: 'left',
                      letterSpacing: '0.5px',
                      fontSize: '10px !important',
                    }}
                  >
                    {`Didn't receieved OTP? `}
                    <Button
                      onClick={doSendOTP}
                      variant="text"
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: '10px',
                        letterSpacing: '0.5px',
                        textDecoration: 'underline',
                        verticalAlign: 'baseline',
                        p: 0,
                        minWidth: 'auto',
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    >
                      Resend again
                    </Button>
                  </Typography>
                )}
              </>
            )}
            {step === 3 && (
              <>
                <Field
                  as={TextField}
                  name="name"
                  label="Full Name *"
                  fullWidth
                  margin="normal"
                  onChange={handleChange}
                  value={values.name}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <Field
                  as={TextField}
                  name="email"
                  label="Email Address (optional)"
                  fullWidth
                  margin="normal"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                <Field
                  as={TextField}
                  name="password"
                  label="Password *"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  value={values.password}
                  onChange={handleChange}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Field
                  as={TextField}
                  name="confirm_password"
                  label="Confirm Password *"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  value={values.confirm_password}
                  onChange={handleChange}
                  error={touched.confirm_password && Boolean(errors.confirm_password)}
                  helperText={touched.confirm_password && errors.confirm_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Terms Checkbox */}
                <FormControlLabel
                  control={<Field as={Checkbox} name="terms" color="primary" />}
                  label={
                    <Typography variant="body2">
                      I agree to the{' '}
                      <Link href="/" underline="hover">
                        Terms & Conditions
                      </Link>{' '}
                      and Privacy Policy
                    </Typography>
                  }
                />
                {touched.terms && errors.terms && (
                  <Typography color="error" variant="body2">
                    {errors.terms}
                  </Typography>
                )}
              </>
            )}

            {/* Submit Button */}
            <Button
              type="button"
              variant="contained"
              fullWidth
              sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
              onClick={(e) => onsubmit(e)}
              disabled={!isValid || !dirty || isSubmitting}
            >
              {step === 1 ? 'Send OTP' : step === 2 ? 'Verify OTP' : 'Create Account'}
            </Button>
            {alertMessage.message !== '' && (
              <Box sx={{ my: 1 }}>
                <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
              </Box>
            )}

            {/* Sign In Link */}
            <Typography align="center" sx={{ mt: 3 }}>
              Already have an account?{' '}
              <Button
                onClick={loginCallback}
                variant="text"
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                Sign In
              </Button>
            </Typography>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default RegisterForm;
